// Rental comp report data helpers.
// Mirrors the sale-oriented formatPropertyData / getDerived in ./utils.js, but
// reads rent fields (last_listed_rent_price_all / _date_all / _status_all) and
// drops sale-only concepts (sold price, discount, cash/financed buyers) plus
// the days-on-market stat (no daysOnMarketHeading → PropertyCard hides the box).
import { _formatNumber, _capitalizeText, _isHouse } from "../../../../shared/helpers/utils";
import { calculateAverages } from "./utils";

// Monthly rents are small whole-dollar values — never abbreviate to $1.5M like
// _formatPrice does. Always render "$4,500/mo".
export function formatRent(value) {
  if (!value || value <= 0) return "N/A";
  return "$" + Math.round(value).toLocaleString("en-US") + "/mo";
}

// Rent PSF is a small per-sqft monthly figure (e.g. $4.52), so keep 2 decimals
// rather than rounding to a whole dollar like _formatNumber does.
export function formatRentPSF(value) {
  if (!value || value <= 0) return "N/A";
  return "$" + Number(value).toFixed(2);
}

// Rental analog of formatPropertyData(). `type` is "active" | "sold" (off market),
// matching the sale signature so the report view's call sites stay identical.
export function formatRentalPropertyData(input, isSubject, type) {
  if (!input) return false;

  const rentStatus = (input.last_listed_rent_status_all || "").toLowerCase();
  // "active" = currently listed for rent; anything else is treated as off market/closed.
  // We only ever have the last listed (asking) rent — never a confirmed rented price.
  const isActive = type === "active" || (type !== "sold" && rentStatus === "active");

  const rentPrice = input.last_listed_rent_price_all;
  const price = rentPrice > 0 ? formatRent(rentPrice) : "N/A";

  let priceType = isActive ? "Asking Rent" : "Off Market Rent";
  if (isSubject) priceType = "Last " + priceType;

  const psfValue = input["div(last_listed_rent_price_all, sq_ft)"];
  const psf = psfValue > 0 && input.sq_ft && input.sq_ft > 1 ? formatRentPSF(psfValue) : "N/A";

  let psfType = isActive ? "Asking Rent PSF" : "Off Market Rent PSF";
  if (isSubject) psfType = "Last " + psfType;

  const now = Date.now() / 1000; // current time in seconds
  function validTs(ts) {
    return ts > 0 && ts <= now;
  }

  const date = validTs(input.last_listed_rent_date_all)
    ? new Date(input.last_listed_rent_date_all * 1000).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric"
      })
    : false;

  return {
    id: input.id || "comp1",
    isSubject: isSubject,
    currentStatus: isActive ? "For Rent" : "Off Market",
    img: "https://newsapi.marketproof.com/buildings/pictures/" + input.building,
    title:
      input.display_full_street_address +
      (input.address_2_normalized ? " - " + input.address_2_normalized.toUpperCase() : ""),
    location:
      input.neighborhood && input.neighborhood.length > 0 ? _capitalizeText(input.neighborhood[0]) : "",
    type:
      _isHouse(input) && input.ownership_type === "rental"
        ? "House/Townhouse"
        : input.ownership_type
          ? _capitalizeText(input.ownership_type)
          : "Unknown",
    yearBuilt: input.year_built > 0 ? input.year_built : "N/A",
    bedsBaths: input.bedrooms > -1 ? input.bedrooms + " Bed / " + input.baths + " Bath" : "--",
    size: input.sq_ft && input.sq_ft > 1 ? input.sq_ft.toLocaleString() + " SF" : "N/A",
    price: price,
    priceType: priceType,
    psf: psf,
    psfType: psfType,
    date: date,
    tag: isSubject ? "SUBJECT" : "COMP",
    buildingAmenities: input.building_features || [],
    unitFeature: input.property_features || [],
    floor: input.floor || null,
    distance: input["geodist()"] != null ? `${(input["geodist()"] * 0.621371).toFixed(2)} mi` : null
  };
}

// Rental analog of getDerived(). Returns the same shape the report view
// destructures, with sale-only fields nulled out and a suggested *rent*.
export const getRentalDerived = (theReportData, activeCompsOverride, soldCompsOverride) => {
  if (!theReportData) return;

  let subjectProperty = theReportData.compSettings.propertyDetails.property;
  subjectProperty = Object.assign(subjectProperty, theReportData.compSettings.propertyDetails);

  subjectProperty["suggestedPrice"] =
    theReportData && theReportData.compResponse && theReportData.compResponse.suggestedPrice
      ? theReportData.compResponse.suggestedPrice
      : 0;
  subjectProperty["suggestedPricePPSF"] =
    subjectProperty["sq_ft"] > 1 ? subjectProperty["suggestedPrice"] / subjectProperty["sq_ft"] : false;
  subjectProperty["suggestedPriceLow"] = subjectProperty["suggestedPrice"] * 0.9;
  subjectProperty["suggestedPriceHigh"] = subjectProperty["suggestedPrice"] * 1.1;

  let avgDOM = theReportData.compResponse.avgDOM ? theReportData.compResponse.avgDOM : null;

  let activeComps = activeCompsOverride ? activeCompsOverride : theReportData.compResponse.comps.active.slice(0, 3);
  let avgActiveComps = calculateAverages(activeComps, "last_listed_rent_price_all", "sq_ft");
  let soldComps = soldCompsOverride ? soldCompsOverride : theReportData.compResponse.comps.sold.slice(0, 3);
  let avgSoldComps = calculateAverages(soldComps, "last_listed_rent_price_all", "sq_ft");

  let activityResponse = theReportData.compResponse.activityResponse
    ? theReportData.compResponse.activityResponse
    : null;

  const { buildingRes } = theReportData;
  if (!subjectProperty["building"] && buildingRes && buildingRes.key) {
    subjectProperty["building"] = buildingRes.key;
  }

  return {
    subjectProperty,
    avgDOM,
    avgDiscount: null, // no rental analog
    activeComps,
    avgActiveComps,
    soldComps,
    avgSoldComps,
    currentCashBuyers: null, // sale-only, dropped for rentals
    currentFinancedBuyers: null,
    percentCashBuyers: null,
    percentFinanced: null,
    activityResponse,
    isCoop: false,
    isRental: true
  };
};
